import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Flame, BookOpen, Users, Crown, ArrowUpRight } from "lucide-react";

export default function Landing() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Nav */}
      <nav className="glass-nav sticky top-0 z-30" data-testid="landing-nav">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#E07A5F] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-semibold text-lg tracking-tight">Bloom</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-[#2D3142] hover:text-[#E07A5F] transition-colors" data-testid="nav-signin">Sign in</Link>
            <Link to="/login" className="pill-btn bg-[#2D3142] text-white text-sm hover:bg-[#E07A5F]" data-testid="nav-get-started">
              Get started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 rise-in">
          <span className="tag-chip bg-[#F2CC8F]/40 text-[#8A5A2B]">A quiet corner of the internet</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-5 text-[#2D3142]">
            Grow gently.<br/>
            <em className="not-italic text-[#E07A5F]">Show up daily.</em><br/>
            Bloom together.
          </h1>
          <p className="mt-6 text-lg text-[#5b5f6e] leading-relaxed max-w-xl">
            Bloom is a personal-growth and creator community for motivation, healing, goal-setting,
            and daily accountability — powered by warm AI reflections and a kind community.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/login" className="pill-btn bg-[#E07A5F] text-white font-medium hover:bg-[#D26A4F] shadow-[0_10px_30px_rgba(224,122,95,0.35)]" data-testid="hero-cta-primary">
              Start free — begin today
            </Link>
            <Link to="/login" className="pill-btn border border-[#2D3142]/15 text-[#2D3142] hover:bg-white" data-testid="hero-cta-secondary">
              Explore Premium →
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-6 text-sm text-[#8A8D98]">
            <div className="flex -space-x-2">
              {["https://i.pravatar.cc/40?img=32","https://i.pravatar.cc/40?img=48","https://i.pravatar.cc/40?img=13","https://i.pravatar.cc/40?img=25"].map((s,i)=>(
                <img key={i} src={s} alt="" className="w-8 h-8 rounded-full border-2 border-[#FDFBF7]" />
              ))}
            </div>
            <p>4,200+ people showed up today</p>
          </div>
        </div>

        <div className="lg:col-span-5 relative rise-in rise-delay-2">
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_60px_rgba(45,49,66,0.15)]">
            <img
              src="https://images.pexels.com/photos/20464241/pexels-photo-20464241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Reflection at sunset"
              className="w-full h-[540px] object-cover"
            />
            <div className="absolute bottom-4 left-4 right-4 bg-white/85 backdrop-blur-md rounded-2xl p-4 border border-black/5">
              <p className="text-xs uppercase tracking-[0.2em] text-[#E07A5F] font-semibold">Today's affirmation</p>
              <p className="font-display text-lg mt-1 leading-snug text-[#2D3142]">
                I move at the pace of my own healing, and that is enough.
              </p>
            </div>
          </div>
          <div className="absolute -top-4 -right-3 bg-[#81B29A] text-white rounded-2xl px-4 py-3 shadow-lg rotate-3">
            <p className="text-xs uppercase tracking-widest">Streak</p>
            <p className="font-display text-3xl leading-none">27 <span className="text-sm">days</span></p>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {[
            { icon: Sparkles, title: "Daily affirmations", body: "AI-written, warm and grounded — never cheesy.", span: "md:col-span-3", tint: "#F2CC8F" },
            { icon: Flame,    title: "Habit streaks",       body: "Track the tiny, quiet wins that stack.",      span: "md:col-span-3", tint: "#E07A5F" },
            { icon: BookOpen, title: "Journal with reflections", body: "Write freely. Get a compassionate mirror when you want one.", span: "md:col-span-4", tint: "#81B29A" },
            { icon: Users,    title: "Kind community",      body: "Follow creators, share wins, cheer each other on.", span: "md:col-span-2", tint: "#2D3142" },
          ].map((f, i) => (
            <div key={i} className={`soft-card p-8 rise-in rise-delay-${(i%4)+1} ${f.span}`} data-testid={`feature-${i}`}>
              <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-5" style={{ background: f.tint + "22", color: f.tint }}>
                <f.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display text-2xl tracking-tight">{f.title}</h3>
              <p className="text-[#5b5f6e] mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Premium */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="soft-card p-10 sm:p-14 relative overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-10 items-center relative z-10">
            <div>
              <span className="tag-chip bg-[#2D3142] text-[#F2CC8F]"><Crown className="w-3 h-3" /> Premium</span>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">Unlock deeper practice.</h2>
              <p className="text-[#5b5f6e] mt-4 leading-relaxed max-w-lg">
                Get unlimited AI reflections, exclusive multi-week challenges, downloadable journals,
                and the Creator Kit — templates, prompts and hooks for building your audience.
              </p>
              <ul className="mt-6 space-y-2 text-[#2D3142]">
                {["Unlimited AI journal reflections","21-day healing series","Creator content templates","Ad-free forever"].map((b,i)=>(
                  <li key={i} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[#E07A5F]"/> {b}</li>
                ))}
              </ul>
              <div className="flex items-baseline gap-2 mt-8">
                <span className="font-display text-5xl">$4.99</span>
                <span className="text-[#8A8D98]">/month · cancel anytime</span>
              </div>
              <Link to="/login" className="mt-6 inline-flex pill-btn bg-[#E07A5F] text-white hover:bg-[#D26A4F] shadow-[0_10px_30px_rgba(224,122,95,0.35)]" data-testid="premium-cta">
                Sign in to upgrade <ArrowUpRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1604079628040-94301bb21b91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHdhcm0lMjBncmFkaWVudCUyMHRleHR1cmUlMjBoZWFsaW5nfGVufDB8fHx8MTc4NzIyNjY3Mnww&ixlib=rb-4.1.0&q=85" alt="Warm glow" className="rounded-3xl w-full h-[360px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E3DF]">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between text-sm text-[#8A8D98]">
          <p>© {new Date().getFullYear()} Bloom — grow gently.</p>
          <p>Made with care for creators & communities.</p>
        </div>
      </footer>
    </div>
  );
}
